import { useState } from 'react';
import { Tabs, Tab, Button, Card, Badge } from 'react-bootstrap';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { RJSFSchema, IconButtonProps } from '@rjsf/utils';
import './ValidationStyles.css';
import { Customization } from '../types';

// Custom button templates for better styling
function AddButton(props: IconButtonProps) {
  return (
    <Button
      variant="success"
      size="sm"
      onClick={props.onClick}
      disabled={props.disabled}
      className="mt-2"
    >
      <i className="bi bi-plus-circle me-1"></i>
      Add {props.title || 'Item'}
    </Button>
  );
}

function RemoveButton(props: IconButtonProps) {
  return (
    <Button
      variant="danger"
      size="sm"
      onClick={props.onClick}
      disabled={props.disabled}
      className="ms-2"
    >
      <i className="bi bi-trash me-1"></i>
      Remove
    </Button>
  );
}

// Custom array field template for better visual separation
function ArrayFieldTemplate(props: any) {
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-bold">{props.title}</label>
        {props.canAdd && (
          <Button
            variant="success"
            size="sm"
            onClick={props.onAddClick}
            disabled={props.disabled || props.readonly}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add {props.title}
          </Button>
        )}
      </div>
      {props.items && props.items.length > 0 && (
        <div className="border rounded p-3 bg-light">
          {props.items.map((element: any) => (
            <Card key={element.key} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">{element.children}</div>
                  {element.hasRemove && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={element.onDropIndexClick(element.index)}
                      disabled={element.disabled || element.readonly}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
      {props.items && props.items.length === 0 && (
        <div className="text-muted fst-italic">
          No {props.title?.toLowerCase()} added yet. Click "Add {props.title}" to create one.
        </div>
      )}
    </div>
  );
}

// Custom object field template for nested objects
function ObjectFieldTemplate(props: any) {
  const isTopLevel = props.idSchema.$id === 'root';

  if (isTopLevel) {
    return <div>{props.properties.map((prop: any) => prop.content)}</div>;
  }

  return (
    <Card className="mb-3">
      <Card.Header className="bg-white">
        <strong>{props.title}</strong>
        {props.description && (
          <small className="text-muted d-block">{props.description}</small>
        )}
      </Card.Header>
      <Card.Body>{props.properties.map((prop: any) => prop.content)}</Card.Body>
    </Card>
  );
}

// Custom field template to show inline validation errors
function FieldTemplate(props: any) {
  const {
    id,
    classNames,
    label,
    help,
    required,
    errors,
    children,
    displayLabel,
    rawErrors,
  } = props;

  // Check for errors in multiple ways (RJSF can pass errors differently)
  const errorList = rawErrors || (errors && errors.props && errors.props.errors) || [];
  const hasErrors = errorList && errorList.length > 0;
  const fieldClassName = `form-group ${classNames} ${hasErrors ? 'field-error' : ''}`;

  return (
    <div className={fieldClassName}>
      {displayLabel && label && (
        <label htmlFor={id} className={`form-label ${required ? 'required' : ''}`}>
          {label}
        </label>
      )}
      {children}
      {hasErrors && (
        <div className="field-error-message">
          {errorList.map((error: string, index: number) => (
            <div key={index} className="error-detail">
              {error}
            </div>
          ))}
        </div>
      )}
      {help}
    </div>
  );
}

const customTemplates = {
  ButtonTemplates: {
    AddButton,
    RemoveButton,
  },
  ArrayFieldTemplate,
  ObjectFieldTemplate,
  FieldTemplate,
};

interface CustomizationTabsProps {
  customizations: Customization[];
  customizationSchema: RJSFSchema;
  onChange: (customizations: Customization[]) => void;
  disabled?: boolean;
  readonly?: boolean;
  showValidation?: boolean;
}

const customizationTypeLabels: Record<string, string> = {
  'hosted-zone': 'Hosted Zone',
  'api-gateway-prereq': 'API Gateway',
  'invoice-unit': 'Invoice Unit',
  'account-tags': 'Account Tags',
};

export function CustomizationTabs({
  customizations,
  customizationSchema,
  onChange,
  disabled,
  readonly,
  showValidation: _showValidation = false,
}: CustomizationTabsProps) {
  const [activeTab, setActiveTab] = useState<string>('0');

  const handleAddCustomization = () => {
    const newCustomization: any = {
      enabled: true,
      parameters: {},
    };
    const newCustomizations = [...customizations, newCustomization];
    onChange(newCustomizations);
    setActiveTab(String(newCustomizations.length - 1));
  };

  const handleRemoveCustomization = (index: number) => {
    const newCustomizations = customizations.filter((_, i) => i !== index);
    onChange(newCustomizations);

    // Adjust active tab if needed
    const newIndex = Math.min(index, newCustomizations.length - 1);
    if (newIndex >= 0) {
      setActiveTab(String(newIndex));
    }
  };

  const handleCustomizationChange = (index: number, data: Customization) => {
    const newCustomizations = [...customizations];
    newCustomizations[index] = data;
    onChange(newCustomizations);
  };

  const getCustomizationLabel = (cust: Customization, index: number) => {
    const label = cust.type 
      ? customizationTypeLabels[cust.type] || cust.type
      : `Customisation ${index + 1}`;
    return (
      <>
        {label}{' '}
        <Badge bg={cust.enabled ? 'success' : 'secondary'} className="ms-1">
          {cust.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </>
    );
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <label className="form-label fw-bold mb-0">Customisations</label>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleAddCustomization}
          disabled={disabled || readonly}
        >
          <i className="bi bi-plus-circle me-1"></i>
          Add Customisation
        </Button>
      </div>

      {customizations.length === 0 ? (
        <div className="text-muted fst-italic border rounded p-3 bg-light">
          No customisations added yet. Click "Add Customisation" to create one.
        </div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => k && setActiveTab(k)}
              className="border-bottom"
            >
              {customizations.map((cust, index) => (
                <Tab
                  key={index}
                  eventKey={String(index)}
                  title={getCustomizationLabel(cust, index)}
                >
                  <div className="p-3">
                    {!readonly && (
                      <div className="d-flex justify-content-end mb-3">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveCustomization(index)}
                          disabled={disabled}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Remove Customisation
                        </Button>
                      </div>
                    )}
                    
                    <Form
                      schema={customizationSchema}
                      formData={cust}
                      validator={validator}
                      onChange={(data) => {
                        if (data.formData) {
                          // Simple: if type changed for this customization, clear its parameters
                          if (cust.type !== data.formData.type) {
                            console.log(`🔄 Customization ${index} type: "${cust.type}" → "${data.formData.type}" - clearing parameters`);
                            data.formData.parameters = {};
                          }
                          handleCustomizationChange(index, data.formData);
                        }
                      }}
                      templates={customTemplates}
                      disabled={disabled}
                      readonly={readonly}
                      liveValidate={false}
                      showErrorList={false}
                      noHtml5Validate
                    >
                      <div></div>
                    </Form>
                  </div>
                </Tab>
              ))}
            </Tabs>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
